import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Divider, Spin } from 'antd';
import { Formik, Form, Field, ErrorMessage as FormikError } from 'formik';
import * as Yup from 'yup';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../../../services/firebase';
import { useLazyValidateQuery } from '../../../redux/slices/auth';
import { FcGoogle } from 'react-icons/fc';

import {
  OuterContainer,
  FormContainer,
  ErrorMessage,
  Title,
  ButtonWrapper,
  Label,
  LogoContainer,
  FormItem,
  StyledInput,
  StyledError,
} from './styled';
import { useIsAuthenticated } from 'services/useAuth';

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated, initialized } = useIsAuthenticated();
  const [validateToken] = useLazyValidateQuery();

  useEffect(() => {
    if (initialized && isAuthenticated) {
      const redirectPath = localStorage.getItem('redirect_path') || '/';
      localStorage.removeItem('redirect_path');
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, initialized, navigate]);

  const initialValues = {
    email: '',
    password: '',
  };

  const validationSchema = Yup.object({
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const handleSuccessfulLogin = async (token: string) => {
    try {
      const validationResult = await validateToken(token).unwrap();
      if (validationResult) {
        localStorage.setItem('access_token', token);
        localStorage.setItem('firebase_id_token', token);
        window.dispatchEvent(new Event('storage'));
        setError('');
        // Optionally redirect here if needed
      } else {
        setError('Invalid token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('firebase_id_token');
      }
    } catch (err) {
      setError('Failed to validate session');
      localStorage.removeItem('access_token');
      localStorage.removeItem('firebase_id_token');
      throw err;
    }
  };

  const handleEmailLogin = async (values: { email: string; password: string }) => {
    setLoading(true);
    setError('');
    try {
      const userCredential = await signInWithEmailAndPassword(auth, values.email, values.password);
      const token = await userCredential.user.getIdToken();
      await handleSuccessfulLogin(token);
    } catch (err: any) {
      setError(err.message || 'Login failed');
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      const token = await userCredential.user.getIdToken();
      await handleSuccessfulLogin(token);
    } catch (err: any) {
      setError(err.message || 'Google login failed');
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    return (
      <OuterContainer>
        <Spin size="large" />
      </OuterContainer>
    );
  }

  return (
    <OuterContainer>
      <FormContainer>
        <LogoContainer>
          <img
            src="https://staging.labeler.icardio.ai/static/media/logo.bca2567da2abc5deb5bddebc704bfe00.svg"
            alt="iCardio.ai Logo"
          />
        </LogoContainer>

        <Title>Sign in to Atlas</Title>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleEmailLogin}
        >
          {() => (
            <Form>
              <FormItem>
                <Label htmlFor="email">Email</Label>
                <Field as={StyledInput} name="email" type="email" placeholder="Enter your email" />
                <FormikError name="email" component={StyledError} />
              </FormItem>

              <FormItem>
                <Label htmlFor="password">Password</Label>
                <Field
                  as={StyledInput}
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                />
                <FormikError name="password" component={StyledError} />
              </FormItem>

              <ButtonWrapper>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--accent)',
                    borderColor: 'var(--accent)',
                  }}
                >
                  Login
                </Button>
              </ButtonWrapper>

              <Divider style={{ borderColor: 'var(--line)' }} />

              <ButtonWrapper>
                <Button
                  icon={React.createElement(FcGoogle as React.ComponentType<{ size?: number }>)}
                  onClick={handleGoogleLogin}
                  loading={loading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                  }}
                >
                  Sign in with Google
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormContainer>
    </OuterContainer>
  );
};

export default Login;
